import { AbstractControl, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { RAMP_MODE } from './Auth';
import { NETWORKS } from '../../core/models/Crypto';

/**
 * Si se quiere AGREGAR NUEVO CAMPO se deberá:
 * 1. Agregar su identificador en FIELD
 * 2. Agregarlo en OnboardingFields.fields
 * 3. Especificar a que step pertenece en OnboardingFields.fieldsBySteps
 */

export enum FIELD {
  //ID
  DNI = 'DNI',
  RUT = 'RUT',
  EMAIL = 'EMAIL',
  PHONE = 'PHONE',

  //PERSONAL DATA
  NAME = 'NAME',
  SURNAME = 'SURNAME',
  COMPANY_NAME = 'COMPANY_NAME',
  ADDRESS_STREET = 'ADDRESS_STREET',
  ADDRESS_NUMBER = 'ADDRESS_NUMBER',
  ADDRESS_FLOOR = 'ADDRESS_FLOOR',
  BIRTH_DATE = 'BIRTH_DATE',
  POSTAL_CODE = 'POSTAL_CODE',
  SEX = 'SEX',
  MARITAL_STATE = 'MARITAL_STATE',
  PROVINCE = 'PROVINCE',
  REGION = 'REGION',
  NATIONALITY = 'NATIONALITY',
  CITY = 'CITY',
  COMMUNE = 'COMMUNE',
  TYC = 'TYC',
  FACTA = 'FACTA',
  UIF = 'UIF',
  PEP = 'PEP',

  //BANKS
  BANK_ACCOUNT_CODE = 'BANK_ACCOUNT_CODE',
  BANK_ACCOUNT_NAME = 'BANK_ACCOUNT_NAME',
  BANK_CODE = 'BANK_CODE',
  BANK_ACCOUNT_TYPE = 'BANK_ACCOUNT_TYPE',
}

export enum OnboardingStep {
  LEGAL_ID,
  DATA_CONFIRM,
  BANK_INFO,
}

export enum ONBOARDING_FLOW {
  WORLDCOIN,
  TC_FLOW,
}

export interface IRequiredFlowFields {
  [ONBOARDING_FLOW.WORLDCOIN]: FIELD[];
  [ONBOARDING_FLOW.TC_FLOW]: FIELD[];
}

export enum EXCHANGE_COUNTRY {
  ARG = 'ARG',
  CHL = 'CHL',
}

export interface OnboardingField {
  validators: ValidatorFn[];
  isPersonal: boolean;
  isCompany: boolean;
  defaultValue: any;

  /** Si no está seteado. El Field le pertenecerá a todos los paises */
  supportedCountries?: EXCHANGE_COUNTRY[];
}

export class OnboardingFields {
  static fields: { [field: string]: OnboardingField } = {
    //ID
    [FIELD.DNI]: {
      validators: [
        Validators.required,
        Validators.maxLength(11),
        this.minLengthDni,
        this.getCuilSufix,
      ],
      isCompany: true,
      isPersonal: true,
      defaultValue: '',
      supportedCountries: [EXCHANGE_COUNTRY.ARG],
    },
    [FIELD.RUT]: {
      validators: [Validators.required, this.validateRUT],
      isCompany: false,
      isPersonal: true,
      defaultValue: '',
      supportedCountries: [EXCHANGE_COUNTRY.CHL],
    },
    [FIELD.PHONE]: {
      validators: [Validators.required, this.phoneLengthValidator, Validators.maxLength(10)],
      isCompany: false,
      isPersonal: true,
      defaultValue: '',
    },
    [FIELD.EMAIL]: {
      validators: [Validators.required, Validators.email, Validators.maxLength(50)],
      isCompany: false,
      isPersonal: true,
      defaultValue: '',
    },

    //PERSONAL DATA
    [FIELD.TYC]: {
      validators: [Validators.requiredTrue],
      isCompany: true,
      isPersonal: true,
      defaultValue: false,
    },

    [FIELD.NAME]: {
      validators: [Validators.required, Validators.maxLength(40), this.invalidCharactersValidator],
      isCompany: false,
      isPersonal: true,
      defaultValue: '',
    },
    [FIELD.SURNAME]: {
      validators: [Validators.required, Validators.maxLength(40), this.invalidCharactersValidator],
      isCompany: false,
      isPersonal: true,
      defaultValue: '',
    },
    [FIELD.COMPANY_NAME]: {
      validators: [Validators.required],
      isCompany: true,
      isPersonal: false,
      defaultValue: '',
    },
    [FIELD.ADDRESS_STREET]: {
      validators: [Validators.required],
      isCompany: true,
      isPersonal: true,
      defaultValue: '',
    },
    [FIELD.ADDRESS_NUMBER]: {
      validators: [Validators.required],
      isCompany: true,
      isPersonal: true,
      defaultValue: '',
    },
    [FIELD.ADDRESS_FLOOR]: {
      validators: [],
      isCompany: true,
      isPersonal: true,
      defaultValue: '',
    },
    [FIELD.POSTAL_CODE]: {
      validators: [Validators.required],
      isCompany: true,
      isPersonal: true,
      defaultValue: '',
    },
    [FIELD.BIRTH_DATE]: {
      validators: [Validators.required, this.requiredDate],
      isCompany: false,
      isPersonal: true,
      defaultValue: null,
    },
    [FIELD.SEX]: {
      validators: [Validators.required],
      isCompany: false,
      isPersonal: true,
      defaultValue: undefined,
    },
    [FIELD.MARITAL_STATE]: {
      validators: [Validators.required],
      isCompany: false,
      isPersonal: true,
      defaultValue: undefined,
    },
    [FIELD.PROVINCE]: {
      validators: [Validators.required],
      isCompany: true,
      isPersonal: true,
      defaultValue: undefined,
      supportedCountries: [EXCHANGE_COUNTRY.ARG],
    },
    [FIELD.REGION]: {
      validators: [Validators.required],
      isCompany: true,
      isPersonal: true,
      defaultValue: '',
      supportedCountries: [EXCHANGE_COUNTRY.CHL],
    },
    [FIELD.NATIONALITY]: {
      validators: [Validators.required],
      isCompany: true,
      isPersonal: true,
      defaultValue: undefined,
    },
    [FIELD.CITY]: {
      validators: [Validators.required],
      isCompany: true,
      isPersonal: true,
      defaultValue: '',
      supportedCountries: [EXCHANGE_COUNTRY.ARG],
    },
    [FIELD.COMMUNE]: {
      validators: [Validators.required],
      isCompany: true,
      isPersonal: true,
      defaultValue: '',
      supportedCountries: [EXCHANGE_COUNTRY.CHL],
    },
    [FIELD.FACTA]: {
      validators: [Validators.requiredTrue],
      isCompany: false,
      isPersonal: true,
      defaultValue: false,
    },
    [FIELD.UIF]: {
      validators: [Validators.required],
      isCompany: false,
      isPersonal: true,
      defaultValue: false,
    },
    [FIELD.PEP]: {
      validators: [Validators.required],
      isCompany: false,
      isPersonal: true,
      defaultValue: false,
    },

    //BANKS
    [FIELD.BANK_ACCOUNT_CODE]: {
      validators: [Validators.required, this.CBUAliasValidator],
      isCompany: true,
      isPersonal: true,
      defaultValue: '',
    },
    [FIELD.BANK_ACCOUNT_NAME]: {
      validators: [
        Validators.required,
        Validators.minLength(3),
        this.invalidCharactersValidator,
        Validators.maxLength(50),
      ],
      isCompany: true,
      isPersonal: true,
      defaultValue: '',
    },
    [FIELD.BANK_ACCOUNT_TYPE]: {
      validators: [Validators.required],
      isCompany: true,
      isPersonal: true,
      defaultValue: undefined,
      supportedCountries: [EXCHANGE_COUNTRY.CHL],
    },
    [FIELD.BANK_CODE]: {
      validators: [Validators.required],
      isCompany: true,
      isPersonal: true,
      defaultValue: undefined,
      supportedCountries: [EXCHANGE_COUNTRY.CHL],
    },
  };

  static fieldsBySteps: { [step: number]: IRequiredFlowFields } = {
    [OnboardingStep.LEGAL_ID]: {
      [ONBOARDING_FLOW.WORLDCOIN]: [FIELD.DNI, FIELD.RUT, FIELD.EMAIL],
      [ONBOARDING_FLOW.TC_FLOW]: [FIELD.DNI, FIELD.PHONE],
    },
    [OnboardingStep.DATA_CONFIRM]: {
      [ONBOARDING_FLOW.WORLDCOIN]: [
        FIELD.NAME,
        FIELD.SURNAME,
        FIELD.COMPANY_NAME,
        FIELD.ADDRESS_STREET,
        FIELD.ADDRESS_NUMBER,
        FIELD.ADDRESS_FLOOR,

        FIELD.BIRTH_DATE,
        FIELD.POSTAL_CODE,
        FIELD.SEX,
        FIELD.MARITAL_STATE,
        FIELD.PROVINCE,
        FIELD.REGION,
        FIELD.NATIONALITY,
        FIELD.CITY,
        FIELD.COMMUNE,
        FIELD.TYC,
        FIELD.FACTA,
        FIELD.UIF,
        FIELD.PEP,
      ],
      [ONBOARDING_FLOW.TC_FLOW]: [
        FIELD.NAME,
        FIELD.SURNAME,
        FIELD.COMPANY_NAME,
        FIELD.ADDRESS_STREET,
        FIELD.ADDRESS_NUMBER,
        FIELD.ADDRESS_FLOOR,
        FIELD.CITY,
        FIELD.BIRTH_DATE,
        FIELD.POSTAL_CODE,
        FIELD.SEX,
        FIELD.MARITAL_STATE,
        FIELD.PROVINCE,
        FIELD.NATIONALITY,
        FIELD.TYC,
        FIELD.FACTA,
        FIELD.UIF,
        FIELD.PEP,
      ],
    },
    [OnboardingStep.BANK_INFO]: {
      [ONBOARDING_FLOW.WORLDCOIN]: [
        FIELD.BANK_ACCOUNT_CODE,
        FIELD.BANK_ACCOUNT_TYPE,
        FIELD.BANK_CODE,
      ],
      [ONBOARDING_FLOW.TC_FLOW]: [FIELD.BANK_ACCOUNT_CODE, FIELD.BANK_ACCOUNT_NAME],
    },
  };

  static getByStep(
    step: OnboardingStep,
    flow: ONBOARDING_FLOW,
    isCompany?: boolean,
    requiredCountry?: EXCHANGE_COUNTRY,
  ): FIELD[] {
    const stepFieldNames = this.fieldsBySteps[step];
    const flowFieldNames = stepFieldNames ? stepFieldNames[flow] : [];
    return flowFieldNames.filter((fieldName) => {
      const field = this.fields[fieldName];

      if (
        field.supportedCountries?.length &&
        !field.supportedCountries.includes(requiredCountry ?? EXCHANGE_COUNTRY.ARG)
      )
        return false;

      return isCompany ? field.isCompany : field.isPersonal;
    });
  }

  static loadControlsToFormGroup(
    formGroup: FormGroup,
    step: OnboardingStep,
    flow: ONBOARDING_FLOW,
    isCommerce?: boolean,
    country?: EXCHANGE_COUNTRY,
  ) {
    OnboardingFields.getByStep(step, flow, isCommerce, country).forEach((fieldName) => {
      const field = OnboardingFields.fields[fieldName];

      const control = new FormControl(field.defaultValue, field.validators);

      if (fieldName === FIELD.POSTAL_CODE) {
        control.addValidators(
          country === EXCHANGE_COUNTRY.CHL ? this.minLengthPostalCodeChl : this.minLengthPostalCode,
        );
        control.updateValueAndValidity();
      }

      formGroup.addControl(fieldName, control);
    });
  }

  static minLengthDni(control: AbstractControl): { [key: string]: boolean } | null {
    return control.value && control.value.length !== 11 ? { cuilMin: true } : null;
  }

  static getCuilSufix(control: AbstractControl): { [key: string]: boolean } | null {
    const cuit = control.value.toString();
    let acumulado = 0;
    const digitos = cuit.split('');
    const digito = Number(digitos.pop());

    if (cuit.startsWith('99')) return null;

    for (let i = 0; i < digitos.length; i++) {
      acumulado += parseInt(digitos[9 - i], 10) * (2 + (i % 6));
    }

    let verif = 11 - (acumulado % 11);
    if (verif === 11) {
      verif = 0;
    }
    if (verif !== digito) {
      return { badCUIT: true };
    }
    return null;
  }

  static validateRUT(control: AbstractControl): { [key: string]: boolean } | null {
    let RUT = control.value;
    const mutipliers = [2, 3, 4, 5, 6, 7];
    let productsSum = 0;

    if (![8, 9].includes(RUT.length)) return { badRUTLength: true };

    let digits = RUT.toString().toUpperCase().split('');
    let lastDigit = digits.pop().toString();

    digits.reverse().forEach((digit, index) => {
      const multiplier = mutipliers[index >= 6 ? index - 6 : index];
      productsSum += multiplier * digit;
    });

    let verifier = (11 - (productsSum - 11 * Math.trunc(productsSum / 11))).toString();

    if (verifier === '11') verifier = '0';
    else if (verifier === '10') verifier = 'K';

    if (lastDigit !== verifier) return { badRUT: true };

    return null;
  }

  static phoneLengthValidator(control: AbstractControl): { [key: string]: boolean } | null {
    return control.value && control.value.toString().length < 10 ? { phoneShort: true } : null;
  }

  static invalidCharactersValidator(control: AbstractControl): { [key: string]: boolean } | null {
    const invalidCharsRegex =
      /^([ 'A-Za-z\u00AA\u00B5\u00BA\u00C0-\u00D6\u2018\u00D8-\u00F6\u00F8-\u02C1\u02C6-\u02D1\u02E0-\u02E4\u02EC\u02EE\u0370-\u0374\u0376\u0377\u037A-\u037D\u037F\u0386\u0388-\u038A\u038C\u038E-\u03A1\u03A3-\u03F5\u03F7-\u0481\u048A-\u052F\u0531-\u0556\u0559\u0561-\u0587\u05D0-\u05EA\u05F0-\u05F2\u0620-\u064A\u066E\u066F\u0671-\u06D3\u06D5\u06E5\u06E6\u06EE\u06EF\u06FA-\u06FC\u06FF\u0710\u0712-\u072F\u074D-\u07A5\u07B1\u07CA-\u07EA\u07F4\u07F5\u07FA\u0800-\u0815\u081A\u0824\u0828\u0840-\u0858\u08A0-\u08B4\u0904-\u0939\u093D\u0950\u0958-\u0961\u0971-\u0980\u0985-\u098C\u098F\u0990\u0993-\u09A8\u09AA-\u09B0\u09B2\u09B6-\u09B9\u09BD\u09CE\u09DC\u09DD\u09DF-\u09E1\u09F0\u09F1\u0A05-\u0A0A\u0A0F\u0A10\u0A13-\u0A28\u0A2A-\u0A30\u0A32\u0A33\u0A35\u0A36\u0A38\u0A39\u0A59-\u0A5C\u0A5E\u0A72-\u0A74\u0A85-\u0A8D\u0A8F-\u0A91\u0A93-\u0AA8\u0AAA-\u0AB0\u0AB2\u0AB3\u0AB5-\u0AB9\u0ABD\u0AD0\u0AE0\u0AE1\u0AF9\u0B05-\u0B0C\u0B0F\u0B10\u0B13-\u0B28\u0B2A-\u0B30\u0B32\u0B33\u0B35-\u0B39\u0B3D\u0B5C\u0B5D\u0B5F-\u0B61\u0B71\u0B83\u0B85-\u0B8A\u0B8E-\u0B90\u0B92-\u0B95\u0B99\u0B9A\u0B9C\u0B9E\u0B9F\u0BA3\u0BA4\u0BA8-\u0BAA\u0BAE-\u0BB9\u0BD0\u0C05-\u0C0C\u0C0E-\u0C10\u0C12-\u0C28\u0C2A-\u0C39\u0C3D\u0C58-\u0C5A\u0C60\u0C61\u0C85-\u0C8C\u0C8E-\u0C90\u0C92-\u0CA8\u0CAA-\u0CB3\u0CB5-\u0CB9\u0CBD\u0CDE\u0CE0\u0CE1\u0CF1\u0CF2\u0D05-\u0D0C\u0D0E-\u0D10\u0D12-\u0D3A\u0D3D\u0D4E\u0D5F-\u0D61\u0D7A-\u0D7F\u0D85-\u0D96\u0D9A-\u0DB1\u0DB3-\u0DBB\u0DBD\u0DC0-\u0DC6\u0E01-\u0E30\u0E32\u0E33\u0E40-\u0E46\u0E81\u0E82\u0E84\u0E87\u0E88\u0E8A\u0E8D\u0E94-\u0E97\u0E99-\u0E9F\u0EA1-\u0EA3\u0EA5\u0EA7\u0EAA\u0EAB\u0EAD-\u0EB0\u0EB2\u0EB3\u0EBD\u0EC0-\u0EC4\u0EC6\u0EDC-\u0EDF\u0F00\u0F40-\u0F47\u0F49-\u0F6C\u0F88-\u0F8C\u1000-\u102A\u103F\u1050-\u1055\u105A-\u105D\u1061\u1065\u1066\u106E-\u1070\u1075-\u1081\u108E\u10A0-\u10C5\u10C7\u10CD\u10D0-\u10FA\u10FC-\u1248\u124A-\u124D\u1250-\u1256\u1258\u125A-\u125D\u1260-\u1288\u128A-\u128D\u1290-\u12B0\u12B2-\u12B5\u12B8-\u12BE\u12C0\u12C2-\u12C5\u12C8-\u12D6\u12D8-\u1310\u1312-\u1315\u1318-\u135A\u1380-\u138F\u13A0-\u13F5\u13F8-\u13FD\u1401-\u166C\u166F-\u167F\u1681-\u169A\u16A0-\u16EA\u16F1-\u16F8\u1700-\u170C\u170E-\u1711\u1720-\u1731\u1740-\u1751\u1760-\u176C\u176E-\u1770\u1780-\u17B3\u17D7\u17DC\u1820-\u1877\u1880-\u18A8\u18AA\u18B0-\u18F5\u1900-\u191E\u1950-\u196D\u1970-\u1974\u1980-\u19AB\u19B0-\u19C9\u1A00-\u1A16\u1A20-\u1A54\u1AA7\u1B05-\u1B33\u1B45-\u1B4B\u1B83-\u1BA0\u1BAE\u1BAF\u1BBA-\u1BE5\u1C00-\u1C23\u1C4D-\u1C4F\u1C5A-\u1C7D\u1CE9-\u1CEC\u1CEE-\u1CF1\u1CF5\u1CF6\u1D00-\u1DBF\u1E00-\u1F15\u1F18-\u1F1D\u1F20-\u1F45\u1F48-\u1F4D\u1F50-\u1F57\u1F59\u1F5B\u1F5D\u1F5F-\u1F7D\u1F80-\u1FB4\u1FB6-\u1FBC\u1FBE\u1FC2-\u1FC4\u1FC6-\u1FCC\u1FD0-\u1FD3\u1FD6-\u1FDB\u1FE0-\u1FEC\u1FF2-\u1FF4\u1FF6-\u1FFC\u2071\u207F\u2090-\u209C\u2102\u2107\u210A-\u2113\u2115\u2119-\u211D\u2124\u2126\u2128\u212A-\u212D\u212F-\u2139\u213C-\u213F\u2145-\u2149\u214E\u2183\u2184\u2C00-\u2C2E\u2C30-\u2C5E\u2C60-\u2CE4\u2CEB-\u2CEE\u2CF2\u2CF3\u2D00-\u2D25\u2D27\u2D2D\u2D30-\u2D67\u2D6F\u2D80-\u2D96\u2DA0-\u2DA6\u2DA8-\u2DAE\u2DB0-\u2DB6\u2DB8-\u2DBE\u2DC0-\u2DC6\u2DC8-\u2DCE\u2DD0-\u2DD6\u2DD8-\u2DDE\u2E2F\u3005\u3006\u3031-\u3035\u303B\u303C\u3041-\u3096\u309D-\u309F\u30A1-\u30FA\u30FC-\u30FF\u3105-\u312D\u3131-\u318E\u31A0-\u31BA\u31F0-\u31FF\u3400-\u4DB5\u4E00-\u9FD5\uA000-\uA48C\uA4D0-\uA4FD\uA500-\uA60C\uA610-\uA61F\uA62A\uA62B\uA640-\uA66E\uA67F-\uA69D\uA6A0-\uA6E5\uA717-\uA71F\uA722-\uA788\uA78B-\uA7AD\uA7B0-\uA7B7\uA7F7-\uA801\uA803-\uA805\uA807-\uA80A\uA80C-\uA822\uA840-\uA873\uA882-\uA8B3\uA8F2-\uA8F7\uA8FB\uA8FD\uA90A-\uA925\uA930-\uA946\uA960-\uA97C\uA984-\uA9B2\uA9CF\uA9E0-\uA9E4\uA9E6-\uA9EF\uA9FA-\uA9FE\uAA00-\uAA28\uAA40-\uAA42\uAA44-\uAA4B\uAA60-\uAA76\uAA7A\uAA7E-\uAAAF\uAAB1\uAAB5\uAAB6\uAAB9-\uAABD\uAAC0\uAAC2\uAADB-\uAADD\uAAE0-\uAAEA\uAAF2-\uAAF4\uAB01-\uAB06\uAB09-\uAB0E\uAB11-\uAB16\uAB20-\uAB26\uAB28-\uAB2E\uAB30-\uAB5A\uAB5C-\uAB65\uAB70-\uABE2\uAC00-\uD7A3\uD7B0-\uD7C6\uD7CB-\uD7FB\uF900-\uFA6D\uFA70-\uFAD9\uFB00-\uFB06\uFB13-\uFB17\uFB1D\uFB1F-\uFB28\uFB2A-\uFB36\uFB38-\uFB3C\uFB3E\uFB40\uFB41\uFB43\uFB44\uFB46-\uFBB1\uFBD3-\uFD3D\uFD50-\uFD8F\uFD92-\uFDC7\uFDF0-\uFDFB\uFE70-\uFE74\uFE76-\uFEFC\uFF21-\uFF3A\uFF41-\uFF5A\uFF66-\uFFBE\uFFC2-\uFFC7\uFFCA-\uFFCF\uFFD2-\uFFD7\uFFDA-\uFFDC])+$/u;
    if (control.value && !invalidCharsRegex.test(control.value.toString().trim())) {
      return { badCharacters: true };
    }
    return null;
  }

  static requiredDate(control: AbstractControl): { [key: string]: boolean } | null {
    if (!control.value || control.value === null) {
      return { required: true };
    }
    return null;
  }

  static minLengthPostalCode(control: AbstractControl): { [key: string]: boolean } | null {
    if (control.value && control.value.length < 4) {
      return { minlengthfour: true };
    }
    return null;
  }

  static minLengthPostalCodeChl(control: AbstractControl): { [key: string]: boolean } | null {
    if (control.value && control.value.length < 7) {
      return { minlengthseven: true };
    }
    return null;
  }

  static CBUAliasValidator(control: AbstractControl): { [key: string]: boolean } | null {
    const invalidCharsRegex = /^([ A-Za-z0-9\u002D\u002E])+$/u;
    if (control.value) {
      if (control.value.length < 6) {
        return { minlength: true };
      }
      if (control.value.length > 22) {
        return { maxlength: true };
      }
      if (control.value && !invalidCharsRegex.test(control.value.toString().trim())) {
        return { badCharacters: true };
      }
    }
    return null;
  }
}

export interface OnboardingState {
  coin?: 'USDC' | 'WLD';
  cryptoAmount?: number;
  successRedirectUrl?: string;
  userId?: string;
  address?: string;
  exchangeCountry?: string;
  flow?: ONBOARDING_FLOW;
  ramp?: RAMP_MODE;
  fiatAmount?: number;
  companyId?: WHITELABEL_COMPANY;
  hideChat?: boolean;
  network?: NETWORKS;
  externalId?: string;
}

export enum WHITELABEL_COMPANY {
  WORLDCOIN = '6579c1d289ad6a000767524f',
  BEETRANSFER = '65bd0f55f6a4760007a00e0f',
}

export interface WhitelabelRegisterRequest {
  address: string;
  userId: string;
  exchangeCountry?: string;
  companyId?: WHITELABEL_COMPANY;
}

export const ACCOUNT_TYPE_BANK_CHILE = [
  {
    value: 'Cuenta corriente',
    label: 'Cuenta corriente',
  },
  {
    value: 'Cuenta de ahorros',
    label: 'Cuenta de ahorros',
  },
  {
    value: 'Cuenta Vista',
    label: 'Cuenta vista',
  },
];

export const BANK_CODE_CHILE = [
  { value: '10', label: 'Bancoestado' },
  { value: '7', label: 'Banco Santander' },
  { value: '16', label: 'Banco Falabella' },
  { value: '13', label: 'Banco BBVA' },
  { value: '14', label: 'Banco Consorcio' },
  { value: '11', label: 'Banco de Chile' },
  { value: '1', label: 'Banco Internacional' },
  { value: '8', label: 'Banco Itaú' },
  { value: '15', label: 'Banco Ripley' },
  { value: '17', label: 'Banco Security' },
  { value: '3', label: 'BCI' },
  { value: '5', label: 'BICE' },
  { value: '12', label: 'Coopeuch' },
  { value: '987', label: 'Copec Pay' },
  { value: '4', label: 'Corpbanca' },
  { value: '996', label: 'Global Card S.A.' },
  { value: '6', label: 'HSBC Bank Chile' },
  { value: '990', label: 'La Polar Prepago' },
  { value: '989', label: 'Mercado Pago' },
  { value: '988', label: 'Prepago los Héroes' },
  { value: '995', label: 'Rabobank' },
  { value: '2', label: 'Scotiabank Chile' },
  { value: '986', label: 'Tapp Caja los Andes' },
  { value: '997', label: 'Tenpo Prepago' },
  { value: '9', label: 'The Bank of Tokyo Mitsubishi UFJ' },
];
